import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

interface UIState {
  // Theme
  theme: 'light' | 'dark' | 'system'
  
  // Layout
  sidebarCollapsed: boolean
  sidebarWidth: number
  editorWidth: number
  
  // Editor preferences
  fontSize: number
  fontFamily: string
  lineHeight: number
  showWordCount: boolean
  enableVimMode: boolean
  enableSpellcheck: boolean
  
  // Auto-save
  autoSaveInterval: number // in seconds
  
  // Note sorting
  defaultNoteSort: 'updated_at' | 'created_at' | 'title' | 'word_count'
  defaultNoteOrder: 'asc' | 'desc'
  
  // UI feedback
  showSaveIndicator: boolean
  saveStatus: 'idle' | 'saving' | 'saved' | 'error'
  lastSaved: Date | null
}

interface UIActions {
  // Theme
  setTheme: (theme: 'light' | 'dark' | 'system') => void
  
  // Layout
  toggleSidebar: () => void
  setSidebarWidth: (width: number) => void
  setEditorWidth: (width: number) => void
  
  // Editor preferences
  setFontSize: (size: number) => void
  setFontFamily: (family: string) => void
  setLineHeight: (height: number) => void
  toggleWordCount: () => void
  toggleVimMode: () => void
  toggleSpellcheck: () => void
  
  // Auto-save
  setAutoSaveInterval: (interval: number) => void
  
  // Note sorting
  setDefaultNoteSort: (sort: UIState['defaultNoteSort']) => void
  setDefaultNoteOrder: (order: UIState['defaultNoteOrder']) => void
  
  // UI feedback
  setSaveStatus: (status: UIState['saveStatus']) => void
  updateLastSaved: () => void
  
  // Reset
  resetToDefaults: () => void
}

export type UIStore = UIState & UIActions

const DEFAULT_STATE: UIState = {
  theme: 'light',
  sidebarCollapsed: false,
  sidebarWidth: 280,
  fontSize: 16,
  fontFamily: 'system-ui',
  lineHeight: 1.6,
  showWordCount: true,
  enableVimMode: false,
  enableSpellcheck: true,
  autoSaveInterval: 2,
  defaultNoteSort: 'updated_at',
  defaultNoteOrder: 'desc',
  showSaveIndicator: true,
  saveStatus: 'idle',
  lastSaved: null,
  editorWidth: 800
}

export const useUIStore = create<UIStore>()(
  persist(
    immer((set) => ({
      ...DEFAULT_STATE,

      // Theme
      setTheme: (theme) => {
        set((state) => {
          state.theme = theme
        })
        
        // Apply theme to document
        const root = document.documentElement
        if (theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
          root.classList.toggle('dark', systemTheme === 'dark')
        } else {
          root.classList.toggle('dark', theme === 'dark')
        }
      },

      // Layout
      toggleSidebar: () => {
        set((state) => {
          state.sidebarCollapsed = !state.sidebarCollapsed
        })
      },

      setSidebarWidth: (width) => {
        set((state) => {
          state.sidebarWidth = Math.max(200, Math.min(400, width))
        })
      },

      setEditorWidth: (width) => {
        set((state) => {
          state.editorWidth = Math.max(400, Math.min(1200, width))
        })
      },

      // Editor preferences
      setFontSize: (size) => {
        set((state) => {
          state.fontSize = Math.max(12, Math.min(24, size))
        })
      },

      setFontFamily: (family) => {
        set((state) => {
          state.fontFamily = family
        })
      },

      setLineHeight: (height) => {
        set((state) => {
          state.lineHeight = Math.max(1.2, Math.min(2.5, height))
        })
      },

      toggleWordCount: () => {
        set((state) => {
          state.showWordCount = !state.showWordCount
        })
      },

      toggleVimMode: () => {
        set((state) => {
          state.enableVimMode = !state.enableVimMode
        })
      },

      toggleSpellcheck: () => {
        set((state) => {
          state.enableSpellcheck = !state.enableSpellcheck
        })
      },

      // Auto-save
      setAutoSaveInterval: (interval) => {
        set((state) => {
          state.autoSaveInterval = Math.max(1, Math.min(60, interval))
        })
      },

      // Note sorting
      setDefaultNoteSort: (sort) => {
        set((state) => {
          state.defaultNoteSort = sort
        })
      },

      setDefaultNoteOrder: (order) => {
        set((state) => {
          state.defaultNoteOrder = order
        })
      },

      // UI feedback
      setSaveStatus: (status) => {
        set((state) => {
          state.saveStatus = status
        })
      },

      updateLastSaved: () => {
        set((state) => {
          state.lastSaved = new Date()
          state.saveStatus = 'saved'
        })
      },

      // Reset
      resetToDefaults: () => {
        set(() => ({ ...DEFAULT_STATE }))
      }
    })),
    {
      name: 'memora-ui-store',
      partialize: (state) => ({
        // Only persist user preferences, not UI state
        theme: state.theme,
        sidebarWidth: state.sidebarWidth,
        editorWidth: state.editorWidth,
        fontSize: state.fontSize,
        fontFamily: state.fontFamily,
        lineHeight: state.lineHeight,
        showWordCount: state.showWordCount,
        enableVimMode: state.enableVimMode,
        enableSpellcheck: state.enableSpellcheck,
        autoSaveInterval: state.autoSaveInterval,
        defaultNoteSort: state.defaultNoteSort,
        defaultNoteOrder: state.defaultNoteOrder,
        showSaveIndicator: state.showSaveIndicator
      })
    }
  )
)

// Initialize theme on app start
if (typeof window !== 'undefined') {
  const store = useUIStore.getState()
  store.setTheme(store.theme)
  
  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    const currentTheme = useUIStore.getState().theme
    if (currentTheme === 'system') {
      document.documentElement.classList.toggle('dark', e.matches)
    }
  })
}